/**
 * Boolean choice, optionally with label and supporting text; supports
 * indeterminate for table select-all.
 * Spec: assets/handoffs/design_system_export/specs/Checkbox.json
 *
 * The spec's box.size/box.sizeMd (16/20) aren't tied to a named prop in the
 * source spec — modeled here as `size` ('sm'|'md', default 'sm') to match
 * the sibling Radio component's identical pattern.
 *
 * @param {'sm'|'md'=sm} size
 * @param {boolean=false} checked
 * @param {boolean=false} indeterminate
 * @param {boolean=false} disabled
 * @param {string=} label
 * @param {string=} supportingText
 * @param {function=} onChange
 */
import React, { forwardRef, useEffect, useRef } from "react";
import classNames from "classnames";
import Icon from "../icons/Icon";
import styles from "./Checkbox.module.scss";

const Checkbox = forwardRef(
  (
    {
      size = "sm",
      checked = false,
      indeterminate = false,
      disabled = false,
      label,
      supportingText,
      className,
      id,
      ...rest
    },
    ref
  ) => {
    const innerRef = useRef(null);

    useEffect(() => {
      if (innerRef.current) {
        innerRef.current.indeterminate = indeterminate;
      }
    }, [indeterminate]);

    const setRefs = (node) => {
      innerRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    };

    return (
      <label
        className={classNames(styles.root, { [styles["is-disabled"]]: disabled }, className)}
        htmlFor={id}
      >
        <span className={classNames(styles.boxWrap, styles[`boxWrap--${size}`])}>
          <input
            ref={setRefs}
            id={id}
            type="checkbox"
            className={styles.input}
            checked={checked}
            disabled={disabled}
            aria-checked={indeterminate ? "mixed" : checked}
            {...rest}
          />
          <span className={styles.box} aria-hidden="true">
            {indeterminate ? (
              <span className={styles.dash} />
            ) : (
              checked && <Icon name="Check" size={12} className={styles.check} />
            )}
          </span>
        </span>
        {(label || supportingText) && (
          <span className={styles.textStack}>
            {label && <span className={styles.label}>{label}</span>}
            {supportingText && (
              <span className={styles.supportingText}>{supportingText}</span>
            )}
          </span>
        )}
      </label>
    );
  }
);

Checkbox.displayName = "Checkbox";

export default Checkbox;
